'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  SourceCodeIcon,
  Globe02Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Collapsible } from '../../../../components/collapsible';
import { Text } from '../../../../components/styled/text';
import { Tooltip } from '../../../../components/tooltip';
import { ReadOnlyCodeEditor } from '../../components/ReadOnlyCodeEditor';
import { TraceFileBlock } from './TraceFileBlock';
import { HttpArtifact } from '../../../artifacts/http/HttpArtifact';
import { useTraceOptional } from '../TraceFilesProvider';
import { useTraceViewSettings } from '../TraceViewSettingsContext';
import { type TraceToolCall } from '../types';
import { normalizeToolName } from '../utils/tool-config';
import { findRelatedTraceFiles } from '../utils/trace-files';
import { shouldShowTraceToolShimmer } from '../utils/trace-text-shimmer';

type CodeExecutionToolCardProps = {
  toolCall: TraceToolCall;
  displayName: string;
};
const SAFE_CODER_ERROR_MESSAGE = 'An error occurred during python execution.';

export function CodeExecutionToolCard({
  toolCall,
  displayName,
}: CodeExecutionToolCardProps) {
  const t = useTranslations('designSystem.chat');
  const traceContext = useTraceOptional();
  const { shouldShowHttpView, setCardHttpView } = useTraceViewSettings();
  const [isOpen, setIsOpen] = useState(false);
  // Get HTTP view state for this card
  const showHttpView = shouldShowHttpView(toolCall.id);
  const normalizedName = normalizeToolName(toolCall.name);
  const allTraceFiles = traceContext?.traceFiles;
  const coderToolCallIndex = 0;
  const relatedFiles = findRelatedTraceFiles(
    toolCall,
    traceContext?.findTraceFiles,
    traceContext?.getTraceFile,
    allTraceFiles,
    coderToolCallIndex,
  );
  const parseExecutionResult = (content: string) => {
    try {
      const parsed = JSON.parse(content) as Record<string, unknown>;

      return {
        success:
          typeof parsed.success === 'boolean' ? parsed.success : undefined,
      };
    } catch {
      return { success: undefined };
    }
  };

  const { success: executionSuccess } = relatedFiles.resultFile
    ? parseExecutionResult(relatedFiles.resultFile.content)
    : { success: undefined };
  const isCoderTool =
    normalizedName === 'coder' ||
    normalizedName === 'sub-agent-coder' ||
    normalizedName === 'coder-tool';
  const isFailedToolCall = toolCall.status === 'error';
  const failureMessage = toolCall.error || SAFE_CODER_ERROR_MESSAGE;
  const blockedCode =
    typeof toolCall.metadata?.codeBlocked === 'string'
      ? toolCall.metadata.codeBlocked.trim()
      : '';
  const shouldShowFailureMessageOnly = isCoderTool && isFailedToolCall;
  const hasError =
    isFailedToolCall || executionSuccess === false || Boolean(toolCall.error);
  const isCompleted = toolCall.status === 'completed';
  const hasResult =
    Boolean(relatedFiles.resultFile) ||
    Boolean(relatedFiles.codeFile) ||
    Boolean(toolCall.output) ||
    Boolean(toolCall.error);
  const StatusIcon = () => {
    if (hasError)
      return (
        <Box color="fg.error" display="flex" alignItems="center">
          <Cancel01Icon size={10} />
        </Box>
      );

    return null;
  };

  const showShimmer = shouldShowTraceToolShimmer({
    hasResult,
    status: toolCall.status,
  });
  const shimmerStyle = showShimmer
    ? {
        background:
          'linear-gradient(90deg, var(--colors-fg-muted) 25%, var(--colors-fg-subtle) 50%, var(--colors-fg-muted) 75%)',
        backgroundSize: '200% 100%',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        animationTimingFunction: 'linear',
      }
    : undefined;
  const toolNameText = (
    <Text
      fontSize="xs"
      color="fg.muted"
      animationStyle={showShimmer ? 'shimmer' : undefined}
      style={shimmerStyle}
    >
      {displayName}
    </Text>
  );

  if (!hasResult) {
    return (
      <HStack w="full" gap={1.5} alignItems="center" py={1} borderRadius="sm">
        {toolNameText}
        <Box flex={1} />
        <Box display="flex" alignItems="center">
          <StatusIcon />
        </Box>
      </HStack>
    );
  }

  return (
    <Collapsible.Root
      open={isOpen}
      onOpenChange={({ open }) => setIsOpen(open)}
      lazyMount
      unmountOnExit
    >
      <Collapsible.Trigger asChild>
        <HStack
          w="full"
          gap={1.5}
          alignItems="center"
          py={1}
          cursor="pointer"
          borderRadius="sm"
        >
          {toolNameText}

          <Box display="flex" alignItems="center" color="fg.muted">
            {isOpen ? (
              <ArrowDown01Icon size={12} />
            ) : (
              <ArrowRight01Icon size={12} />
            )}
          </Box>

          <Box flex={1} />

          <HStack gap={2} alignItems="center">
            {isCompleted && (
              <HStack
                gap="0"
                borderWidth="1px"
                borderColor="border.default"
                borderRadius="sm"
                overflow="hidden"
                flexShrink={0}
                onClick={(e) => e.stopPropagation()}
              >
                <Tooltip title="Show code">
                  <Box
                    as="button"
                    aria-label="Show code"
                    onClick={() => setCardHttpView(toolCall.id, false)}
                    h="5"
                    w="5"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    cursor="pointer"
                    bg={!showHttpView ? 'bg.muted' : 'transparent'}
                    color={!showHttpView ? 'fg.default' : 'fg.muted'}
                    _hover={{
                      bg: !showHttpView ? 'bg.muted' : 'bg.hover',
                    }}
                  >
                    <SourceCodeIcon size={10} />
                  </Box>
                </Tooltip>
                <Tooltip title="Show HTTP requests">
                  <Box
                    as="button"
                    aria-label="Show HTTP requests"
                    onClick={() => setCardHttpView(toolCall.id, true)}
                    h="5"
                    w="5"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    cursor="pointer"
                    bg={showHttpView ? 'bg.muted' : 'transparent'}
                    color={showHttpView ? 'fg.default' : 'fg.muted'}
                    _hover={{
                      bg: showHttpView ? 'bg.muted' : 'bg.hover',
                    }}
                  >
                    <Globe02Icon size={10} />
                  </Box>
                </Tooltip>
              </HStack>
            )}
            <StatusIcon />
          </HStack>
        </HStack>
      </Collapsible.Trigger>

      <Collapsible.Content>
        <VStack alignItems="stretch" gap={2} width="100%" py={2}>
          {showHttpView && isCompleted ? (
            <Box width="100%" bg="transparent" py={3} overflow="hidden">
              <HttpArtifact
                toolCallId={toolCall.id}
                toolType="PYTHON"
                showSummaryHeader={true}
              />
            </Box>
          ) : shouldShowFailureMessageOnly ? (
            <VStack alignItems="stretch" gap={2} width="100%">
              <Box
                width="100%"
                borderLeftWidth="2px"
                borderLeftColor="border.error"
                pl={2}
                py={2}
              >
                <Text fontSize="xs" color="fg.error">
                  {failureMessage}
                </Text>
              </Box>
              {blockedCode ? (
                <ReadOnlyCodeEditor
                  value={blockedCode}
                  maxHeight="300px"
                  language="python"
                />
              ) : null}
            </VStack>
          ) : (
            <>
              {toolCall.error && (
                <Box
                  width="100%"
                  borderLeftWidth="2px"
                  borderLeftColor="border.error"
                  pl={2}
                  py={2}
                >
                  <Text fontSize="xs" color="fg.error">
                    {toolCall.error}
                  </Text>
                </Box>
              )}

              {relatedFiles.codeFile &&
                normalizedName !== 'sub-agent-intelligence' && (
                  <TraceFileBlock
                    file={relatedFiles.codeFile}
                    label={t('input')}
                    renderMode="codeBlock"
                    contentFontSize={14}
                  />
                )}

              {relatedFiles.resultFile && (
                <TraceFileBlock
                  file={relatedFiles.resultFile}
                  label={
                    normalizedName === 'sub-agent-intelligence'
                      ? ''
                      : t('output')
                  }
                  hasError={hasError}
                  collapsible={normalizedName === 'sub-agent-intelligence'}
                  defaultOpen={false}
                  renderMode="codeBlock"
                  contentFontSize={14}
                />
              )}
            </>
          )}
        </VStack>
      </Collapsible.Content>
    </Collapsible.Root>
  );
}
